import React, { useState } from "react";
import { ChevronRight, Link2, Link2Off } from "lucide-react";
import { cn } from "@/lib/utils";
import { SeverityBadge } from "./SeverityBadge";
import { ReceiptColumns } from "./ReceiptColumns";
import { FullResponseModal } from "./FullResponseModal";
import type { ProviderReceipt, QuestionVerdict } from "./verdictModel";

const SEVERITY_CLASS: Record<QuestionVerdict["severity"], string> = {
  strong: "vb-qcard--strong",
  mid: "vb-qcard--mid",
  weak: "vb-qcard--weak",
};

/** One question's verdict (VB-F1 summary + VB-F2 receipts). Severity-tinted
 *  header, cross-model summary, surfaced-signal pills, and a collapsible
 *  receipts section with the per-model columns + full-response dialog. */
export function QuestionCard({ q, who }: { q: QuestionVerdict; who: string }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<ProviderReceipt | null>(null);

  return (
    <article className={cn("vb-qcard", SEVERITY_CLASS[q.severity])}>
      <div className="vb-qcard__hd">
        <div className="vb-qcard__left">
          <h3 className="vb-qcard__q">{q.text}</h3>
        </div>
        <SeverityBadge severity={q.severity} />
      </div>

      <p className="vb-qcard__summary">{q.summary}</p>

      {q.surfacedSignals.length > 0 && (
        <div className="vb-qcard__signals">
          <span className="vb-qcard__signals-lbl">AI Trust Signals surfaced</span>
          {q.surfacedSignals.map((s) => (
            <span className="vb-signal-pill" key={s.key}>
              {s.name}
            </span>
          ))}
        </div>
      )}

      {q.providers.length > 0 && (
        <div className="vb-qcard__receipts">
          <div className="vb-qcard__receipts-bar">
            <button
              type="button"
              className={cn("vb-receipts-toggle", open && "is-open")}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <ChevronRight size={14} className="vb-receipts-toggle__chev" aria-hidden="true" />
              {open ? (
                "Collapse"
              ) : (
                <>
                  See what AI answered
                  <span className="vb-receipts-toggle__meta">
                    {q.receiptCount} {q.receiptCount === 1 ? "source" : "sources"} ·{" "}
                    {q.providers.length} {q.providers.length === 1 ? "model" : "models"}
                  </span>
                </>
              )}
            </button>

            <span
              className={cn("vb-cited", q.notCited ? "vb-cited--no" : "vb-cited--yes")}
              title={
                q.notCited
                  ? "No model cited your own website when answering this question"
                  : "At least one model cited your own website when answering this question"
              }
            >
              {q.notCited ? (
                <Link2Off size={13} aria-hidden="true" />
              ) : (
                <Link2 size={13} aria-hidden="true" />
              )}
              {q.notCited ? "Your site wasn't cited" : "Your site was cited"}
            </span>
          </div>

          {open && (
            <ReceiptColumns
              providers={q.providers}
              onOpenResponse={(p) => {
                setActive(p);
              }}
            />
          )}
        </div>
      )}

      <FullResponseModal
        open={active != null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
        provider={active}
        question={q.text}
        who={who}
      />
    </article>
  );
}

export default QuestionCard;
